import { OneOffCheckRecord } from '@/types/turn-api';
import { VALIDATION_RETRY_MESSAGE } from './constants';
import { ReportValidationResponse, ReportValidationState, ReportValidationStatus, ValidatedReport } from './types';

export const IDLE_VALIDATION: ReportValidationState = {
  failures: [],
  status: 'idle',
  validated: null,
};

export const requiresReportValidation = (record?: OneOffCheckRecord.Base | null) =>
  Boolean(record?.requires_report_validation) && !record?.validation_overridden;

export const saveBlockedByValidation = (
  record: OneOffCheckRecord.Base | null | undefined,
  fileUrl: string | null,
  status: ReportValidationStatus
) => requiresReportValidation(record) && Boolean(fileUrl) && status !== 'passed';

export const toValidationState = (data?: ReportValidationResponse | null): ReportValidationState => {
  if (typeof data?.valid !== 'boolean') {
    return {
      failures: [],
      status: 'unavailable',
      validated: null,
    };
  }

  if (data.valid) {
    return {
      failures: [],
      status: 'passed',
      validated: data.validated ?? null,
    };
  }

  return {
    failures: data.failures ?? [],
    status: 'failed',
    validated: null,
  };
};

export const humanizeLabel = (value?: string | null) => {
  const text = (value || '').replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  return text.replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const failureBulletText = (failure: { code: string; message: string }) => {
  if (failure.code === 'candidate_mismatch') return `Candidate mismatch — ${failure.message}`;
  if (failure.code === 'report_type_mismatch') return `Report type mismatch — ${failure.message}`;
  return failure.message;
};

export const validationSuccessLines = (
  validated: ValidatedReport | null,
  candidateName?: string | null,
  checkType?: string | null
) => {
  if (!validated) return [];
  const name = candidateName?.trim() || validated.candidate_name_found;
  const typeLabel = humanizeLabel(checkType) || humanizeLabel(validated.one_off_check_type) || validated.document_type;
  return [`Candidate matches ${name}.`, `Report type matches ${typeLabel}.`];
};

export const validationRetryText = (status: ReportValidationStatus) =>
  status === 'unavailable' ? VALIDATION_RETRY_MESSAGE : null;
